import { useState, useMemo } from "react";

/**
 * useSortableTable
 *
 * Generic hook that adds column-sorting to any flat array of objects.
 *
 * @param {Array}  rows        - The original (unsorted) data array.
 * @param {Object} options
 * @param {string} [options.defaultKey]   - Column key to sort by on first render.
 * @param {string} [options.defaultDir]   - "asc" | "desc"  (default: "asc")
 * @param {Object} [options.comparators]  - Optional custom comparator map:
 *                                           { [key]: (a, b) => number }
 *                                          Return negative / zero / positive,
 *                                          exactly like Array.prototype.sort.
 *                                          If omitted, a sensible default is used
 *                                          (locale-aware strings, numeric numbers,
 *                                           ISO-date strings, nulls last).
 *
 * @returns {{ sortedRows, sortKey, sortDir, handleSort }}
 *   sortedRows  - The sorted copy of `rows`.
 *   sortKey     - Currently active column key (string | null).
 *   sortDir     - "asc" | "desc".
 *   handleSort  - (key: string) => void  – call this from a column header click.
 */
export function useSortableTable(rows, options = {}) {
  const {
    defaultKey = null,
    defaultDir = "asc",
    comparators = {},
  } = options;

  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;

    const dir = sortDir === "asc" ? 1 : -1;

    return [...rows].sort((a, b) => {
      // Use a custom comparator if provided for this key
      if (comparators[sortKey]) {
        return dir * comparators[sortKey](a, b);
      }

      const valA = a[sortKey];
      const valB = b[sortKey];

      // Nulls / undefined always sort last
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      // Numbers
      if (typeof valA === "number" && typeof valB === "number") {
        return dir * (valA - valB);
      }

      // ISO date strings (e.g. "2024-03-15T00:00:00Z")
      const dateA = Date.parse(valA);
      const dateB = Date.parse(valB);
      if (!isNaN(dateA) && !isNaN(dateB)) {
        return dir * (dateA - dateB);
      }

      // Strings – locale-aware, case-insensitive
      return dir * String(valA).localeCompare(String(valB), undefined, {
        sensitivity: "base",
        numeric: true,
      });
    });
  }, [rows, sortKey, sortDir, comparators]);

  return { sortedRows, sortKey, sortDir, handleSort };
}